import React, {
  FunctionComponent,
  useState,
  useRef,
  useEffect,
  ChangeEvent,
  useContext,
} from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  EnrollmentFormProps2023,
  EnrollOptionalLabel,
} from "./../EnrollmentFormPage";
import BackNextButtons from "./../BackNextButtons";
import { FocusableHeader, DOBValidator } from "../../../components";
import {
  useTranslate,
  getValidationObject,
  EnrollmentPages,
} from "../../../helpers";
import PlanInfo from "./../PlanInfo";
import { Alert, ChoiceList, TextField } from "@cmsgov/ds-medicare-gov";
import PhoneNumberInput from "./../PhoneNumberInput";
import { Sex, MCTDate, Ethnicity, Race } from "../../../@types";
import {
  removeExtraWhiteSpace,
  shouldShowRaceEthnicitySection,
} from "../../../helpers/enrollmentFormHelpers";
import { RaceEthnicitySection } from "./RaceEthnicitySection";
import { AppContext } from "../../../app/store";

const PersonalInfo: FunctionComponent<EnrollmentFormProps2023> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();
  const flags = useFlags();
  const {
    state: { language },
  } = useContext(AppContext);

  const { tmpFeEnableEnrollmentRaceEthnicity } = flags;
  const [validForm, setValidForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const alertDiv = useRef<HTMLDivElement>(null);

  const [sexError, setSexError] = useState<string | undefined>(undefined);
  const [sexTouched, setSexTouched] = useState(false);
  const [firstNameError, setFirstNameError] = useState<string | undefined>(
    undefined
  );
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameError, setLastNameError] = useState<string | undefined>(
    undefined
  );
  const [middleInitialError, setMiddleInitialError] = useState<
    string | undefined
  >(undefined);
  const [middleInitialTouched, setMiddleInitialTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [dobTouched, setDobTouched] = useState(false);
  const [dobError, setDobError] = useState<string | undefined>(undefined);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const [racesError, setRacesError] = useState<string | undefined>(undefined);
  const [racesTouched, setRacesTouched] = useState(false);
  const [ethnicitiesError, setEthnicitiesError] = useState<string | undefined>(
    undefined
  );
  const [ethnicitiesTouched, setEthnicitiesTouched] = useState(false);

  const showRaceEthnicitySection = shouldShowRaceEthnicitySection(
    plan,
    Boolean(tmpFeEnableEnrollmentRaceEthnicity)
  );

  const validateForm = (): void => {
    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.PersonalInfo,
      flags
    );

    if (!formStatus.firstNameValidity && firstNameTouched) {
      setFirstNameError(t("enroll_form.enter_first_name"));
    } else {
      setFirstNameError(undefined);
    }

    if (!formStatus.middleInitialValidity && middleInitialTouched) {
      setMiddleInitialError(t("enroll_form.enter_middle_initial"));
    } else {
      setMiddleInitialError(undefined);
    }

    if (!formStatus.lastNameValidity && lastNameTouched) {
      setLastNameError(t("enroll_form.enter_last_name"));
    } else {
      setLastNameError(undefined);
    }

    if (!formStatus.dobValidity && dobTouched) {
      setDobError(t("enroll_form.enter_dob"));
    } else {
      setDobError(undefined);
    }

    if (!formStatus.phoneValidity && phoneTouched) {
      setPhoneError(t("enroll_form.enter_phone_number"));
    } else {
      setPhoneError(undefined);
    }

    if (!formStatus.sexValidity && sexTouched) {
      setSexError(t("enroll_form.choose_sex"));
    } else {
      setSexError(undefined);
    }

    if (showRaceEthnicitySection) {
      if (!formStatus.ethnicitiesValidity && ethnicitiesTouched) {
        setEthnicitiesError(t("enroll_form.race_ethnicity_section.error"));
      } else {
        setEthnicitiesError(undefined);
      }
      if (!formStatus.racesValidity && racesTouched) {
        setRacesError(t("enroll_form.race_ethnicity_section.error"));
      } else {
        setRacesError(undefined);
      }
    }

    setValidForm(formStatus.isValid);
  };

  useEffect(() => {
    validateForm();
  }, [
    firstNameTouched,
    lastNameTouched,
    dobTouched,
    sexTouched,
    phoneTouched,
    racesTouched,
    ethnicitiesTouched,
    language,
  ]);

  useEffect(() => {
    if (submitted && !validForm) {
      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }
    }
  }, [submitted]);

  const next = (): void => {
    setSubmitted(true);

    if (!validForm) {
      setDobTouched(true);
      setFirstNameTouched(true);
      setMiddleInitialTouched(true);
      setLastNameTouched(true);
      setSexTouched(true);
      setPhoneTouched(true);
      setRacesTouched(true);
      setEthnicitiesTouched(true);

      return;
    }

    nextPage();
  };

  useEffect(() => {
    validateForm();
  }, [enrollData]);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.your_personal_info")}
      />

      <div className="ds-u-measure--wide">
        <PlanInfo plan={plan} />
        <span className="ds-u-visibility--screen-reader">
          {t("sr_only_additional_questions_may_appear")}
        </span>
        <div ref={alertDiv}>
          {!validForm && submitted && (
            <Alert
              className="ds-u-margin-top--2"
              heading={t("enroll_form.needs_your_attention")}
              variation="error"
            >
              {t("enroll_form.complete_required")}
            </Alert>
          )}
        </div>
        <section>
          <TextField
            label={t("enroll_form.first_name")}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                firstName: e.target.value,
              });
              setFirstNameTouched(true);
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                firstName: removeExtraWhiteSpace(e.target.value),
              });
              setFirstNameTouched(true);
            }}
            errorMessage={firstNameError}
            value={enrollData.firstName}
            name="first-name"
          />

          <TextField
            size="small"
            maxLength={1}
            label={
              <EnrollOptionalLabel label={t("enroll_form.middle_initial")} />
            }
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({ ...enrollData, middleInitial: e.target.value });
              setMiddleInitialTouched(true);
            }}
            errorMessage={middleInitialError}
            onBlur={(): void => setMiddleInitialTouched(true)}
            value={enrollData.middleInitial}
            name="middle-initial"
          />

          <TextField
            label={t("enroll_form.last_name")}
            errorMessage={lastNameError}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                lastName: e.target.value,
              });
              setLastNameTouched(true);
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                lastName: removeExtraWhiteSpace(e.target.value),
              });
              setLastNameTouched(true);
            }}
            value={enrollData.lastName}
            name="last-name"
          />
        </section>
        <section>
          <DOBValidator
            hideAlert={true}
            label={t("csr_landing.dob")}
            onComplete={(date: MCTDate | boolean): void => {
              if (typeof date === "boolean") return;

              setEnrollData({ ...enrollData, dob: date });
              setDobTouched(
                !!date.day &&
                  !!date.month &&
                  !!date.year &&
                  date.year.length === 4
              );
            }}
            defaultDate={enrollData.dob}
            errorMessage={dobError}
          />
        </section>
        <section>
          <ChoiceList
            type="radio"
            label={
              <span className="ds-u-text-transform--uppercase">
                {t("enroll_form.sex")}
              </span>
            }
            name="sex"
            errorMessage={sexError}
            choices={[
              {
                label: t("enroll_form.male"),
                value: Sex.MALE,
                checked: enrollData.sex === Sex.MALE,
              },
              {
                label: t("enroll_form.female"),
                value: Sex.FEMALE,
                checked: enrollData.sex === Sex.FEMALE,
              },
            ]}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                sex: e.target.value as Sex,
              });
            }}
          />
        </section>
        <section>
          <PhoneNumberInput
            phoneNumber={enrollData.primaryPhone}
            name="primary"
            errorMessage={phoneError}
            label={t("enroll.personal.2021.phone_number")}
            onChange={(primaryPhone): void => {
              setEnrollData({ ...enrollData, primaryPhone });
              setPhoneTouched(
                primaryPhone.areaCode.length >= 1 &&
                  primaryPhone.exchangeCode.length >= 1 &&
                  primaryPhone.lineNumber.length === 4
              );
            }}
          />
        </section>
        {showRaceEthnicitySection && (
          <RaceEthnicitySection
            ethnicities={enrollData.ethnicities}
            ethnicitiesError={ethnicitiesError}
            onEthnicitiesChanged={(updatedEthnicities: Ethnicity[]) => {
              setEnrollData({
                ...enrollData,
                ethnicities: updatedEthnicities,
              });
              setEthnicitiesTouched(true);
            }}
            races={enrollData.races}
            racesError={racesError}
            onRacesChanged={(updatedRaces: Race[]) => {
              setEnrollData({
                ...enrollData,
                races: updatedRaces,
              });
              setRacesTouched(true);
            }}
          />
        )}
        <BackNextButtons
          inReview={inReview}
          nextPage={next}
          previousPage={previousPage}
        />
      </div>
    </div>
  );
};

export default PersonalInfo;
